import React from 'react';
import { t } from 'i18next';
import { ListColumnOptionType, ListRowType } from 'types/ListTypes';
import { renderDate, renderDateTime, renderLabelBadges, renderPriceDecimalColumn } from './renderListColumn';
import { renderCustomFieldColumn } from './renderCustomFieldColumn';

/*
 * renderGenericColumn.tsx
 * Builds a generic render function for a user chosen extra column,
 * based on the generic column type the backend resolved for the
 * model field. Foreign key and related object columns render their
 * annotated display value instead of the raw id.
 */

// Build the render function of a generic column by its type
export const buildGenericColumnRender = (option: ListColumnOptionType, userLocale: string, listKey: string, timeFormat: '12h' | '24h' = '24h') => {
    return (value: any, item: ListRowType): JSX.Element => {

        // Render the answer value of a custom field by its field type
        if (option.type === 'custom_field') {
            return renderCustomFieldColumn(option, value, userLocale, timeFormat);
        }

        // Render the annotated display value of a foreign key or related column
        if (option.type === 'fk') {
            const displayValue = item[`${option.name}_display`];
            return <div>{displayValue ?? ''}</div>;
        }

        // Render the labels of the row as badges
        if (option.type === 'labels') {
            const labels = Array.isArray(value) ? value.filter(label => label && label.name) : [];
            return labels.length ? renderLabelBadges(labels) : <div></div>;
        }

        // Render booleans as their translated yes or no value
        if (option.type === 'boolean') {
            return <div>{t(value ? 'forms.true_value' : 'forms.false_value')}</div>;
        }

        // Render an empty cell for missing values
        if (value === null || value === undefined || value === '') {
            return <div></div>;
        }

        // Render a database choice as its translated option value
        if (option.type === 'choice') {
            return <div>{t(`${listKey}.${option.name}.${value}`, { defaultValue: String(value) })}</div>;
        }

        // Render dates and datetimes formatted on the user locale
        if (option.type === 'date') {
            return renderDate(value, userLocale);
        }
        if (option.type === 'datetime') {
            return renderDateTime(value, userLocale);
        }

        // Render money amounts formatted with the row currency, euro when the row has none
        if (option.type === 'amount') {
            return renderPriceDecimalColumn(value, item.currency ?? 'EUR', userLocale);
        }

        // Render numbers and text as their raw value
        return <div>{String(value)}</div>;
    };
};
